export const SENJA_WIDGET_IDS = {
  general: "2aa961c0-36a2-466e-888c-b304486767c9",
  petLocationFree: "50bebe37-acb9-4cca-b7f5-977e41c6f117",
  pet: "fabf98a1-13b4-4aa8-aa28-91b62e090ab1",
  dog: "d7f6f491-3d64-4894-8d0f-e7fe8ce4531c",
  cat: "dda1a64d-e885-42f9-82dc-2eb9c4810e19",
  cost: "ddd4e5fc-e64d-41e7-89ac-88479a7ff3ed",
} as const

export interface SenjaWidgetGroup {
  readonly widgetId: string
  readonly name: string
  readonly heading: string
  readonly filters: string
}

const APPROVED_VIDEO_5_STAR = "approved, video, rating 5, highlights required"

export const SENJA_PAGE_REVALIDATE_SECONDS = 60 * 60

// A failed or over-budget refresh must not keep fallback testimonials for the
// page's full ISR window.
export const SENJA_RETRY_REVALIDATE_SECONDS = 60

export function senjaRevalidateSeconds(
  isCurrent: boolean,
  pageRevalidateSeconds: number,
): number {
  if (!isCurrent) {
    return Math.min(SENJA_RETRY_REVALIDATE_SECONDS, pageRevalidateSeconds)
  }

  return pageRevalidateSeconds
}

// A Record<string, SenjaWidgetGroup> annotation would widen the literal group
// keys to string.
function defineWidgetGroups<T extends Record<string, SenjaWidgetGroup>>(
  groups: T,
): T {
  return groups
}

export const SENJA_WIDGET_GROUPS = defineWidgetGroups({
  // Geo pages need testimonials that do not contradict their pickup or delivery location.
  petLocationFree: {
    widgetId: SENJA_WIDGET_IDS.petLocationFree,
    name:
      "[Pets, pickup and deliver not specified] Multiple Video Testimonials",
    heading: "What Pet Owners Say About Shipping Their Pets",
    filters: `${APPROVED_VIDEO_5_STAR}; collections: pets (location-free)`,
  },
  pet: {
    widgetId: SENJA_WIDGET_IDS.pet,
    name: "[Pets] Multiple Video Testimonials",
    heading: "What Pet Owners Say About Shipping Their Pets",
    filters: `${APPROVED_VIDEO_5_STAR}; collections: all pet species`,
  },
  dog: {
    widgetId: SENJA_WIDGET_IDS.dog,
    name: "[Dogs] Multiple Video Testimonials",
    heading: "What Dog Owners Say About Shipping Their Dogs",
    filters: `${APPROVED_VIDEO_5_STAR}; collections: dogs`,
  },
  cat: {
    widgetId: SENJA_WIDGET_IDS.cat,
    name: "[Cats] Multiple Video Testimonials",
    heading: "What Cat Owners Say About Shipping Their Cats",
    filters: `${APPROVED_VIDEO_5_STAR}; collections: cats`,
  },
  cost: {
    widgetId: SENJA_WIDGET_IDS.cost,
    name: "[Costs Mentioned] Multiple Video Testimonials",
    heading: "What Customers Say About What They Paid",
    filters: `${APPROVED_VIDEO_5_STAR}; collections: costs mentioned`,
  },
  general: {
    widgetId: SENJA_WIDGET_IDS.general,
    name: "[General] Multiple Video Testimonials",
    heading: "What CitizenShipper Customers Say",
    filters: `${APPROVED_VIDEO_5_STAR}; no collection filter`,
  },
})

export type SenjaWidgetGroupKey = keyof typeof SENJA_WIDGET_GROUPS

function definePageGroups<T extends Record<string, SenjaWidgetGroupKey>>(
  groups: T,
): T {
  return groups
}

// Route fallback for stories without a `senjaTestimonials` blok, and the group
// for non-CMS hubs. It also selects the live transcripts appended to those hubs'
// Markdown snapshots; CMS pages convert their current HTML instead.
export const SENJA_PAGE_GROUPS = definePageGroups({
  "/pet-transportation": "pet",
  "/dog-transportation": "dog",
  "/cat-transportation": "cat",
  "/military-moves": "general",
  "/pet-shipping/all-cities": "pet",
  "/dog-shipping/all-breeds": "dog",
  "/cost-to-ship": "cost",
})

export function senjaGroupForPath(
  pathname: string,
): SenjaWidgetGroupKey | undefined {
  // Pathnames such as "constructor" must not resolve inherited
  // Object.prototype members.
  if (!Object.hasOwn(SENJA_PAGE_GROUPS, pathname)) {
    return undefined
  }

  return SENJA_PAGE_GROUPS[pathname as keyof typeof SENJA_PAGE_GROUPS]
}
